import {
  type AccessField,
  type Binary,
  Bool,
  CalendarValue,
  type Call,
  type Convert,
  type ExpressionVisitor,
  type Group,
  Identifier,
  type Negate,
  type Num,
  type Str,
} from './ast.ts';
import { Tokenizer } from './tokenizer.ts';
import { Parser } from './parser.ts';
import type { SubFormulaBrief } from '../../types/release-profiles.ts';

export class VariableNameReplacer implements ExpressionVisitor<string> {
  /**
   * Replaces variables in a formula with the given replacements. To prevent excessive (or infinite) looping, this
   * function only runs 5 replacement passes maximum.
   *
   * If errors occur in tokenizing or parsing, the original formula is returned under the assumption that usage of the
   * formula would fail regardless of replacements at a higher level than where this function is intended to be used.
   */
  static replaceInStringIgnoringErrors(
    formulaOrig: string,
    onIdentifier: (identifier: Identifier) => string | null,
    replacementLoopsRemaining = 5,
  ): string {
    if (replacementLoopsRemaining <= 0) {
      return formulaOrig;
    }

    const tokensOrError = Tokenizer.tokenize(formulaOrig);
    if ('error' in tokensOrError) {
      console.error(`VariableNameReplacer: Unable to tokenize formula "${formulaOrig}", error was:`, tokensOrError);
      return formulaOrig;
    }

    const parsedExpressionOrError = Parser.parseExpression(tokensOrError.tokens);
    if ('error' in parsedExpressionOrError) {
      console.error(
        `VariableNameReplacer: Unable to parse formula "${formulaOrig}", error was:`,
        parsedExpressionOrError,
      );
      return formulaOrig;
    }

    const formulaWithReplacements = parsedExpressionOrError.expression.accept(new VariableNameReplacer(onIdentifier));
    if (formulaWithReplacements === formulaOrig) {
      return formulaOrig;
    } else {
      return VariableNameReplacer.replaceInStringIgnoringErrors(
        formulaWithReplacements,
        onIdentifier,
        replacementLoopsRemaining - 1,
      );
    }
  }

  /**
   * This function substitutes formulas in a given formula string, ignoring errors.
   *
   * It recursively replaces formula references with their definitions, up to a maximum number of replacement loops.
   *
   * If formula references are annotated, the annotation is preserved and added to the variables in the substituted
   * formula.
   *
   * If no formulas are provided, the original formula is returned unchanged.
   */
  static substituteFormulasIgnoringErrors(
    formulaOrig: SubFormulaBrief,
    otherFormulas: Map<string, SubFormulaBrief>,
    annotatableVariableNames: Set<string>,
  ): {
    formulaNew: string;
    subFormulas: SubFormulaBrief[];
  } {
    if (otherFormulas.size === 0) {
      return { formulaNew: formulaOrig.formula, subFormulas: [] };
    }

    const subFormulas: SubFormulaBrief[] = [];

    // First, we find formulas to expand:
    const formulaNew = VariableNameReplacer.replaceInStringIgnoringErrors(formulaOrig.formula, (formulaIdentifier) => {
      if (!otherFormulas.has(formulaIdentifier.nameBare)) {
        return null;
      }

      const subFormula = otherFormulas.get(formulaIdentifier.nameBare)!;

      // If there's no annotation, we can return the formula as-is
      if (!formulaIdentifier.annotation) {
        subFormulas.push({
          nameFriendly: subFormula.nameFriendly,
          nameForVariable: formulaIdentifier.nameBare,
          formula: subFormula.formula,
        });
        return subFormula.formula;
      }

      // If there is an annotation, we need to add it to all eligible variables and formula references:
      const annotationToAdd = '[' + formulaIdentifier.annotation + ']';
      const subFormulaNew = VariableNameReplacer.replaceInStringIgnoringErrors(
        subFormula.formula,
        (subFormulaVariableIdentifier) => {
          if (
            (annotatableVariableNames.has(subFormulaVariableIdentifier.nameBare) ||
              // replaceInStringIgnoringErrors will do multiple passes, so if multiple formulas are involved, the next
              // pass will cause any formulas detected here to be expanded:
              otherFormulas.has(subFormulaVariableIdentifier.nameBare)) &&
            // But, if the variable or formula reference already has an annotation, we don't want to change it:
            !subFormulaVariableIdentifier.annotation
          ) {
            return subFormulaVariableIdentifier.nameBare + annotationToAdd;
          } else {
            return null;
          }
        },
      );
      subFormulas.push({
        nameFriendly: `${subFormula.nameFriendly} (${formulaIdentifier.annotation})`,
        nameForVariable: formulaIdentifier.nameWithAnnotation,
        formula: subFormulaNew,
      });

      return subFormulaNew;
    });

    if (formulaNew !== formulaOrig.formula) {
      // Because we recursively expand formulas, the most deeply-nested formula reference (and therefore the formula
      // with the least dependencies, since formula references form a DAG) will end up being pushed _last_. We want
      // to display sub-formulas in calculation order, so we reverse the list:
      subFormulas.reverse();

      subFormulas.push({
        nameFriendly: formulaOrig.nameFriendly,
        nameForVariable: formulaOrig.nameForVariable,
        formula: formulaOrig.formula,
      });
    }

    return { formulaNew, subFormulas };
  }

  onIdentifier: (identifier: Identifier) => string | null;

  private constructor(onIdentifier: (identifier: Identifier) => string | null) {
    this.onIdentifier = onIdentifier;
  }

  visitNum(node: Num): string {
    return node.toString();
  }

  visitStr(node: Str): string {
    return `'${node.value}'`;
  }

  visitBool(node: Bool): string {
    return node.toString();
  }

  visitCalendarValue(node: CalendarValue): string {
    return node.toString();
  }

  visitIdentifier(node: Identifier): string {
    const replacement = this.onIdentifier(node);
    if (replacement !== null) {
      // If we're replacing one variable name with another name or a number, use the replacement as-is.
      // Otherwise, wrap the new expression in parentheses:
      if (/^[A-Za-z0-9_]+(\[[^\]]+\])?$/.test(replacement)) {
        return replacement;
      } else {
        return '(' + replacement + ')';
      }
    } else {
      return node.nameWithAnnotation;
    }
  }

  visitAccessField(node: AccessField): string {
    return node.expr.accept(this) + '.' + node.name.nameWithAnnotation;
  }

  visitNegate(node: Negate): string {
    return '-' + node.expr.accept(this);
  }

  visitCall(node: Call): string {
    let result: string;
    if (node.callee instanceof Identifier) {
      result = node.callee.nameWithAnnotation;
    } else {
      result = node.callee.accept(this);
    }

    result += '(';
    for (let i = 0; i < node.args.length; i++) {
      const arg = node.args[i];

      result += arg.accept(this);
      if (i < node.args.length - 1) {
        result += ', ';
      }
    }
    result += ')';

    return result;
  }

  visitGroup(node: Group): string {
    return '(' + node.expr.accept(this) + ')';
  }

  visitConvert(node: Convert): string {
    return node.expr.accept(this) + ' -> ' + node.units.toString();
  }

  visitBinary(node: Binary): string {
    return node.left.accept(this) + ' ' + node.operator + ' ' + node.right.accept(this);
  }
}
